/**
 * The console's copy of the banner legibility rule, and the palettes that are always safe.
 *
 * ## Why this is a copy at all
 *
 * The rule itself lives in `packages/contracts/src/contrast.ts` as `checkBannerLegibility`,
 * and the API calls it on every write. That is the authority, and nothing here can widen it:
 * a pair this file accepted and the contract refused would fail on save with the API's own
 * message, which is the correct failure mode.
 *
 * It is duplicated rather than imported because this console imports `@genex/contracts` for
 * **types only** — the tsconfig alias points at the built `.d.ts` and Vite is deliberately not
 * told about it, so the console builds with no workspace build and ships none of the contract
 * package's runtime dependencies. Reaching for one function here would pull zod and the whole
 * schema graph into an operator console to avoid re-typing a documented WCAG formula.
 *
 * The duplication is held honest by `bannerContrast.test.ts`, which imports the contract by
 * source path (a test runs in node, so it can hold the real thing against this copy) and
 * asserts the two page colours match exactly, that the text bar is identical, and that this
 * file's surface bar is never *weaker* than the contract's.
 *
 * Never weaker rather than equal, deliberately, because the two are not equal: the contract
 * separates a banner from the page at 1.5:1 (`SURFACE_SEPARATION`, and its own note explains
 * why it is not WCAG's 3:1), while this console demands the full 3:1. Stricter here is
 * harmless. Weaker here is not: it would offer an operator a pair the API then refuses on
 * save, with an error about a rule the console never showed them. The test also sweeps every
 * pair this file green-lights through the contract's checker, so a threshold edited on one
 * side and not the other fails the build rather than reaching an operator.
 *
 * An earlier version of this comment claimed a test pinned the thresholds by name. None did.
 * Everything imported from this copy, so the copy was only pinned against itself.
 *
 * ## What the rule actually is
 *
 * Three ratios, and the second and third are the ones that matter here because they are the
 * ones an operator physically cannot check. They are looking at one theme. Their customers are
 * split across two.
 *
 *   1. **Text on the banner** — 4.5:1, WCAG AA for body text. Theme-independent: the banner
 *      paints its own opaque background.
 *   2. **Banner on the light page** — 3:1, WCAG AA for a UI component's boundary. A pale
 *      banner passes (1) beautifully with dark text and then has no edge at all on white.
 *   3. **Banner on the dark page** — 3:1, same rule, mirrored. A near-black banner is the same
 *      mistake seen from the other side.
 *
 * The band that clears both (2) and (3) is genuinely narrow — mid-tone and saturated, neither
 * pale nor nearly black — which is exactly why the presets below exist. An operator who does
 * not want to think about contrast should not have to, and should not be able to lose.
 */

/** WCAG 2.1 AA, normal-weight body text. Mirrors `WCAG_AA_TEXT` in the contract. */
export const WCAG_AA_TEXT = 4.5;

/** WCAG 2.1 AA, non-text contrast. Mirrors `WCAG_AA_NON_TEXT` in the contract. */
export const WCAG_AA_NON_TEXT = 3;

/**
 * The two page surfaces a banner is drawn on. Mirrors `APP_SURFACES` in the contract.
 *
 * The dark one is the app's real near-black blue-grey, not `#000000`. Checking against pure
 * black would pass colours that are in fact invisible on the screen a customer is holding.
 */
export const APP_SURFACES = { light: '#FFFFFF', dark: '#12151D' } as const;

const HEX = /^#([0-9A-Fa-f]{6})$/;

/** `#RRGGBB`, upper-cased. Null for anything else — the contract's schema demands this shape. */
export function normaliseHex(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const match = HEX.exec(value.trim());
  return match === null ? null : `#${(match[1] ?? '').toUpperCase()}`;
}

function linearise(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function channelsOf(hex: string): readonly [number, number, number] | null {
  const match = HEX.exec(hex.trim());
  if (match === null) return null;
  const value = Number.parseInt(match[1] ?? '', 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(hex: string): number | null {
  const channels = channelsOf(hex);
  if (channels === null) return null;
  return (
    0.2126 * linearise(channels[0]) +
    0.7152 * linearise(channels[1]) +
    0.0722 * linearise(channels[2])
  );
}

/** Contrast ratio, 1:1 to 21:1. Symmetric: the lighter colour is always the numerator. */
export function contrastRatio(a: string, b: string): number | null {
  const first = relativeLuminance(a);
  const second = relativeLuminance(b);
  if (first === null || second === null) return null;
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

export type LegibilityCheck =
  | 'text_on_background'
  | 'background_on_light'
  | 'background_on_dark'
  | 'malformed';

export interface LegibilityFailure {
  readonly check: LegibilityCheck;
  readonly required: number;
  readonly actual: number | null;
  readonly message: string;
}

export interface LegibilityVerdict {
  readonly ok: boolean;
  readonly failures: readonly LegibilityFailure[];
  /** Every ratio, so the form can show the operator how close they are rather than only yes/no. */
  readonly ratios: {
    readonly textOnBackground: number | null;
    readonly backgroundOnLight: number | null;
    readonly backgroundOnDark: number | null;
  };
}

/** One decimal. More digits imply a precision nobody is judging by. */
function round(value: number | null): number | null {
  return value === null ? null : Math.round(value * 10) / 10;
}

/**
 * Whether a banner's colours are readable, on both themes.
 *
 * Returns **every** failure rather than the first, so an operator who has picked two bad
 * colours fixes both in one pass instead of discovering the second only after correcting the
 * first. The messages name the mode that breaks and the direction to move, because "invalid
 * colours" tells somebody nothing they can act on.
 */
export function checkBannerLegibility(input: {
  readonly backgroundColor: string;
  readonly textColor: string;
}): LegibilityVerdict {
  const textOnBackground = contrastRatio(input.textColor, input.backgroundColor);
  const backgroundOnLight = contrastRatio(input.backgroundColor, APP_SURFACES.light);
  const backgroundOnDark = contrastRatio(input.backgroundColor, APP_SURFACES.dark);

  const ratios = {
    textOnBackground: round(textOnBackground),
    backgroundOnLight: round(backgroundOnLight),
    backgroundOnDark: round(backgroundOnDark),
  };

  const failures: LegibilityFailure[] = [];

  if (textOnBackground === null || backgroundOnLight === null || backgroundOnDark === null) {
    failures.push({
      check: 'malformed',
      required: WCAG_AA_TEXT,
      actual: null,
      message: 'Those are not both readable colours. Use a six digit hex value like #101520.',
    });
    return { ok: false, failures, ratios };
  }

  if (textOnBackground < WCAG_AA_TEXT) {
    failures.push({
      check: 'text_on_background',
      required: WCAG_AA_TEXT,
      actual: ratios.textOnBackground,
      message: `The text is too close in tone to the banner colour to read. It needs ${String(WCAG_AA_TEXT)} to 1 contrast and this pair is ${String(ratios.textOnBackground)} to 1. Pick a lighter text on a dark banner, or a darker text on a light one.`,
    });
  }

  if (backgroundOnLight < WCAG_AA_NON_TEXT) {
    failures.push({
      check: 'background_on_light',
      required: WCAG_AA_NON_TEXT,
      actual: ratios.backgroundOnLight,
      message: `In light mode this banner would disappear into the page. The banner colour needs ${String(WCAG_AA_NON_TEXT)} to 1 against a white background and it is ${String(ratios.backgroundOnLight)} to 1. Choose a deeper colour.`,
    });
  }

  if (backgroundOnDark < WCAG_AA_NON_TEXT) {
    failures.push({
      check: 'background_on_dark',
      required: WCAG_AA_NON_TEXT,
      actual: ratios.backgroundOnDark,
      message: `In dark mode this banner would disappear into the page. The banner colour needs ${String(WCAG_AA_NON_TEXT)} to 1 against a near black background and it is ${String(ratios.backgroundOnDark)} to 1. Choose a brighter colour.`,
    });
  }

  return { ok: failures.length === 0, failures, ratios };
}

export interface BannerPreset {
  readonly key: string;
  readonly label: string;
  readonly backgroundColor: string;
  readonly textColor: string;
}

/**
 * Candidate palettes. Every one is checked at module load; only the survivors are offered.
 *
 * The filter below is not defensive tidiness, it is the point. A preset is the sanctioned,
 * one-click, "I do not want to think about this" answer — so a preset that fails validation is
 * strictly worse than having no presets at all, because it is a button that produces a banner
 * the API will reject and looks like the recommended choice while it does it.
 *
 * The spread is chosen to cover the hues an operator reaches for while staying inside the
 * narrow mid-tone band that clears 3:1 against white *and* against the app's near-black
 * surface. Pale tints and near-blacks are absent because they cannot pass, not because they
 * were not considered.
 */
const CANDIDATE_PRESETS: readonly BannerPreset[] = [
  { key: 'violet', label: 'Violet', backgroundColor: '#6D3FF0', textColor: '#FFFFFF' },
  { key: 'indigo', label: 'Indigo', backgroundColor: '#4C5BD4', textColor: '#FFFFFF' },
  { key: 'ocean', label: 'Ocean', backgroundColor: '#1652F0', textColor: '#FFFFFF' },
  { key: 'azure', label: 'Azure', backgroundColor: '#1668C7', textColor: '#FFFFFF' },
  { key: 'teal', label: 'Teal', backgroundColor: '#0F7E86', textColor: '#FFFFFF' },
  { key: 'forest', label: 'Forest', backgroundColor: '#0E7A4F', textColor: '#FFFFFF' },
  { key: 'moss', label: 'Moss', backgroundColor: '#3E7A2E', textColor: '#FFFFFF' },
  { key: 'olive', label: 'Olive', backgroundColor: '#6E7320', textColor: '#FFFFFF' },
  { key: 'amber', label: 'Amber', backgroundColor: '#8A6A00', textColor: '#FFFFFF' },
  { key: 'clay', label: 'Clay', backgroundColor: '#B4471F', textColor: '#FFFFFF' },
  { key: 'rose', label: 'Rose', backgroundColor: '#C2344A', textColor: '#FFFFFF' },
  { key: 'slate', label: 'Slate', backgroundColor: '#5A6683', textColor: '#FFFFFF' },
];

/** The palettes actually offered: the candidates above that pass, in order. */
export const BANNER_PRESETS: readonly BannerPreset[] = CANDIDATE_PRESETS.filter(
  (preset) =>
    checkBannerLegibility({
      backgroundColor: preset.backgroundColor,
      textColor: preset.textColor,
    }).ok,
);

/** What a brand-new banner starts as, so the form is never in a failing state on open. */
export const DEFAULT_PRESET: BannerPreset = BANNER_PRESETS[0] ?? {
  key: 'ocean',
  label: 'Ocean',
  backgroundColor: '#1652F0',
  textColor: '#FFFFFF',
};

/**
 * The muted partner for the banner's second line, exactly as the app derives it.
 *
 * Duplicated from `bannerBodyColor` in the app so the preview is the pixels a customer gets
 * rather than an approximation of them. Mixing the text colour toward the background can only
 * move within a pair that has already passed, so it cannot introduce a new failure.
 */
export function bannerBodyColor(text: string, background: string): string {
  const fg = channelsOf(text);
  const bg = channelsOf(background);
  if (fg === null || bg === null) return text;
  const channel = (i: number): string =>
    Math.round((fg[i] ?? 0) * 0.78 + (bg[i] ?? 0) * 0.22)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(0)}${channel(1)}${channel(2)}`.toUpperCase();
}

/**
 * Whether an operator's destination is one the app will actually open.
 *
 * `https` only, parsed rather than pattern-matched, and no credentials in the authority. The
 * interesting inputs are the ones that look like a URL to a regex and resolve to something
 * else — `javascript:`, `intent://`, `tel:`, `market://` — and every one of them is a real
 * capability on a phone reachable by whoever holds a console session.
 *
 * The API refuses these too, and the app refuses them a third time before opening. This one
 * exists so the operator finds out *here*, next to the field, where the typo can be fixed,
 * rather than from a customer whose tap did nothing.
 */
export function normaliseExternalUrl(value: string): { url: string } | { error: string } {
  const trimmed = value.trim();
  if (trimmed === '') return { error: 'Enter a web address, or leave it empty.' };
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { error: 'That is not a web address. It should start with https://' };
  }
  if (parsed.protocol !== 'https:') {
    return {
      error:
        'Only https addresses are accepted. The app will not open anything else, so a customer tapping this would get nothing at all.',
    };
  }
  if (parsed.hostname === '') return { error: 'That address has no website in it.' };
  if (parsed.username !== '' || parsed.password !== '') {
    return { error: 'Remove the username and password from the address.' };
  }
  return { url: parsed.toString() };
}
